import { faHashtag, faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  Button,
  Drawer,
  Grid,
  Icon,
  Input,
  Message,
  Tag,
  Tooltip,
} from "@intility/bifrost-react";
import { useState } from "react";
import { useClient, useUpdateClientTags } from "../api/clients";
import type { ClientListItem } from "../api/types";
import { ClientsByTagDrawer } from "./ClientsByTagDrawer";
import { useCanEditClient } from "./useCanEditClient";
import "./ClientTags.css";

// A client's tags in a horizontal row, ending in an "Add tag" tag that opens
// the tag drawer. Tags live on the full client record, not the list item.
export function ClientTags({ client }: { client: ClientListItem }) {
  const { data: detail } = useClient(client.id);
  const updateTags = useUpdateClientTags(client.id);
  const [open, setOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  // The tag whose clients are listed in the by-tag drawer, null when closed.
  const [tagToBrowse, setTagToBrowse] = useState<string | null>(null);

  // The row renders before the record loads, so "Add tag" is always there for
  // an editor; saving waits for `detail`, which the PUT needs to keep the
  // existing tags and purpose.
  const tags = detail?.tags ?? [];
  const canEdit = useCanEditClient(client);
  const trimmed = newTag.trim();
  const duplicate = tags.includes(trimmed);

  function close() {
    if (updateTags.isPending) return;
    setOpen(false);
    setNewTag("");
    updateTags.reset();
  }

  // Adding and removing both save straight away and leave the drawer open, so
  // several tags can be added or cleaned up in one go.
  function addTag() {
    if (!trimmed || duplicate || !detail) return;
    updateTags.mutate(
      { purposeTag: detail.purposeTag ?? "", tags: [...detail.tags, trimmed] },
      { onSuccess: () => setNewTag("") },
    );
  }

  function removeTag(tag: string) {
    if (!detail) return;
    updateTags.mutate({
      purposeTag: detail.purposeTag ?? "",
      tags: detail.tags.filter((t) => t !== tag),
    });
  }

  return (
    <>
      <div className="client-tags">
        {tags.map((tag) => (
          // Anyone who can see the client can see who else carries the tag.
          <Tag key={tag} onClick={() => setTagToBrowse(tag)}>
            <Icon icon={faHashtag} className="client-tag-hash" />
            {tag}
          </Tag>
        ))}
        <Tag
          disabled={!canEdit}
          title={canEdit ? undefined : "You can't edit this client's tags"}
          onClick={() => setOpen(true)}
        >
          Add tag
          <Icon icon={faPlus} marginLeft />
        </Tag>
      </div>

      <ClientsByTagDrawer
        tag={tagToBrowse}
        onClose={() => setTagToBrowse(null)}
      />

      <Drawer
        isOpen={open}
        onRequestClose={close}
        header="Add tag"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="flat"
              onClick={close}
              state={updateTags.isPending ? "inactive" : undefined}
            >
              Close drawer
            </Button>
          </div>
        }
      >
        <Grid gap={16} style={{ marginTop: 24 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Input
                label="Tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add tag…"
                autoFocus
              />
            </div>
            <Button
              variant="filled"
              onClick={addTag}
              state={
                updateTags.isPending || !detail || !trimmed || duplicate
                  ? "inactive"
                  : undefined
              }
            >
              Add tag
            </Button>
          </div>
          {duplicate && (
            <Message state="warning">This client already has that tag.</Message>
          )}
          {tags.length > 0 && (
            <div className="client-tags">
              {tags.map((tag) => (
                <Tooltip key={tag} content="Remove tag">
                  <Tag
                    aria-label={`Remove tag ${tag}`}
                    disabled={updateTags.isPending}
                    onClick={() => removeTag(tag)}
                  >
                    <Icon icon={faHashtag} className="client-tag-hash" />
                    {tag}
                    <Icon icon={faXmark} marginLeft />
                  </Tag>
                </Tooltip>
              ))}
            </div>
          )}
          {updateTags.error && (
            <Message state="alert" header="Could not update tags">
              {updateTags.error instanceof Error
                ? updateTags.error.message
                : "Something went wrong."}
            </Message>
          )}
        </Grid>
      </Drawer>
    </>
  );
}
